import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { setItem } from '@propertypal/shared/src/services/local.storage';
import Accordion from '@propertypal/web-ui/src/animation/Accordion';
import BroadbandTable from '@propertypal/web-ui/src/broadband/BroadbandTable';
import Button from '@propertypal/web-ui/src/buttons/Button';
import ReportProblemButton from '@propertypal/web-ui/src/buttons/ReportProblemButton';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import ImageModal from '@propertypal/web-ui/src/modal/ImageModal';
import AgentAccreditations from '@propertypal/web-ui/src/property/AgentAccreditations';
import ContactTable from '@propertypal/web-ui/src/property/ContactTable';
import DevelopmentHeader from '@propertypal/web-ui/src/property/DevelopmentHeader';
import KeyInfoList from '@propertypal/web-ui/src/property/KeyInfoList';
import MarketTrends from '@propertypal/web-ui/src/property/MarketTrends';
import OtherDevelopments from '@propertypal/web-ui/src/property/OtherDevelopments';
import PromoteProperty from '@propertypal/web-ui/src/property/PromoteProperty';
import PropertyAgentBar from '@propertypal/web-ui/src/property/PropertyAgentBar';
import PropertyDescription from '@propertypal/web-ui/src/property/PropertyDescription';
import PropertySlider from '@propertypal/web-ui/src/property/PropertySlider';
import PropertySummary from '@propertypal/web-ui/src/property/PropertySummary';
import RelatedLinks from '@propertypal/web-ui/src/property/RelatedLinks';
import ShowHomesBar from '@propertypal/web-ui/src/property/ShowHomesBar';
import SimilarProperties from '@propertypal/web-ui/src/property/SimilarProperties';
import SlideshowLinks from '@propertypal/web-ui/src/property/SlideshowLinks';
import PropertyBanner from '@propertypal/web-ui/src/property-box/PropertyBanner';
import Breadcrumbs from '@propertypal/web-ui/src/search/Breadcrumbs';
import { Bold, SubHeading, Text } from '@propertypal/web-ui/src/typography';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React from 'react';
import { useTheme } from 'styled-components';
import { LEGAL_FEES_RESULT_STORAGE_KEY } from '../../hooks/useCachedLegalFees';
import AdBox from '../ads/AdBox';
import { PROPERTY_IMAGE_LOCATOR, PropertyDetailsContentProps, routerUpdate } from './PropertyDetails';
import PropertyPdfBar from './PropertyPdfBar';
import PropertySocialBar from './PropertySocialBar';
import PropertyUnits from './PropertyUnits';

const AD_BOX_2_SIZES = [[728, 90], 'fluid'];
const AD_BOX_3_SIZES = [[320, 100], [300, 250], [336, 280], 'fluid'];

const BrokerCalculator = dynamic(() => import('@propertypal/web-ui/src/property/BrokerCalculator'), { ssr: false });
const LegalFeesCalculator = dynamic(() => import('@propertypal/web-ui/src/property/LegalFeesCalculator'), {
  ssr: false,
});
const BrochureModal = dynamic(() => import('./BrochureModal'), { ssr: false });
const PropertyStats = dynamic(() => import('@propertypal/web-ui/src/property/PropertyStats'), { ssr: false });
const SchoolsData = dynamic(() => import('../schools/SchoolsData'), { ssr: false });
const EnquiryModal = dynamic(() => import('./EnquiryModal'), { ssr: false });
const LegalFeesModal = dynamic(() => import('./LegalFeesModal'), { ssr: false });
const GoogleMap = dynamic(() => import('../map/GoogleMap'), { ssr: false });
const PropertyMapModal = dynamic(() => import('./PropertyMapModal'), { ssr: false });
const PropertySlideshowModal = dynamic(() => import('./PropertySlideshowModal'), { ssr: false });
const ReportProblemModal = dynamic(() => import('./ReportProblemModal'), { ssr: false });
const ShareModal = dynamic(() => import('./ShareModal'), { ssr: false });
const StampDutyModal = dynamic(() => import('./StampDutyModal'), { ssr: false });

const PropertyDetailsContent: React.FC<PropertyDetailsContentProps> = ({
  property,
  loggedIn,
  adSuffix,
  images,
  updateSectionUrl,
  imageIndex,
  updatePhotoUrl,
  handleImageClick,
  showSlideshowModal,
  getReplaceUrl,
  hasSitemap,
  hasFeaturedBrochure,
  urlParts,
  favourited,
  toggleFavourited,
  leadId,
  handleInfoClick,
  buyingStatus,
  callAgent,
  modal,
  showPhoneModal,
  ukResident,
  mortgageCalculator,
  onFavourite,
  handleShare,
  isFeaturedDevelopment,
  similarProperties,
  fetchSimilarProperties,
  showMapModal,
  isMobile,
  openSections,
  renderAccordionHeader,
  showLegalFees,
  hasLegalContact,
  setEnquiryAccountNumber,
  showSchoolData,
  showPromoteText,
  marketTrendsData,
  marketTrendsError,
  marketTrendsLoading,
  fetchMarketTrendsData,
  otherDevelopments,
  fetchOtherDevelopments,
  modalImage,
  setModalImage,
  showStampDutyModal,
  setBuyingStatus,
  setUkResident,
  handleBack,
  showEnquiryModal,
  showRegisterInterestModal,
  showRequestValuationModal,
  enquiryAccountNumber,
  setLeadId,
  showLegalFeesModal,
  showBrochureModal,
  showReportModal,
  showShareModal,
  isAdObscuringModalOpen,
}) => {
  const theme = useTheme();
  const router = useRouter();

  return (
    <div className="pp-details-page">
      <PropertyAgentBar property={property} hideOnMobile />

      {property.showHomeOpeningTime && <ShowHomesBar property={property} />}

      {property.featuredDevelopmentContent?.contentTop && (
        <DevelopmentHeader
          html={property.featuredDevelopmentContent?.contentTop}
          property={property}
          images={images}
          onOpenSlideshowClick={() => updateSectionUrl('/slideshow', 0)}
          onImageClick={(index) => updateSectionUrl('/slideshow', index)}
        />
      )}

      {!property.featuredDevelopmentContent?.contentTop &&
        property.coordinate &&
        !urlParts.includes('map') &&
        images.length === 0 && (
          <GoogleMap
            width="100%"
            height={300}
            lat={property.coordinate.latitude}
            lng={property.coordinate.longitude}
            zoom={property.coordinate.mapZoomLevel}
            markers={[
              {
                id: 1,
                lat: property.coordinate.latitude,
                lng: property.coordinate.longitude,
              },
            ]}
          />
        )}

      {!property.featuredDevelopmentContent?.contentTop && images.length > 0 && (
        <PropertySlider
          alt={property.addressLine1}
          images={images}
          imageIndex={imageIndex}
          setImageIndex={updatePhotoUrl}
          onClick={handleImageClick}
          onCounterClick={handleImageClick}
          centerImageCounter
          renderImageOverlay={() => <PropertyBanner property={property} disableFeatured />}
          enableKeyboardControls={!showSlideshowModal}
          updateOnIndexChange={!showSlideshowModal}
          maxHeight="75vh"
        />
      )}

      <div className="pp-top-section">
        <SlideshowLinks
          getReplaceUrl={getReplaceUrl}
          property={property}
          hasSitemap={hasSitemap}
          hasFeaturedBrochure={hasFeaturedBrochure}
        />

        <Breadcrumbs crumbs={property.breadcrumbs} containerStyle={{ paddingTop: 20 }} />

        <PageWrapper className="pp-summary-box">
          <PropertySummary
            property={property}
            onCallClick={(location) =>
              callAgent(property, undefined, undefined, property.type === 'development' ? 'DDP' : 'PDP', location)
            }
            onShowNumberClick={(location) => {
              showPhoneModal(property, undefined, undefined, property.type === 'development' ? 'DDP' : 'PDP', location);
            }}
            onEnquiryClick={() => updateSectionUrl('/enquiry')}
            onRegisterInterestClick={() => updateSectionUrl('/register-interest')}
            onRequestValuationClick={() => updateSectionUrl('/request-valuation')}
            registerInterest={property.registerInterest}
            requestValuation={property.requestValuation}
            favourited={favourited}
            toggleFavourited={toggleFavourited}
            leadId={leadId}
          />

          {property.keyInfo && (
            <KeyInfoList
              property={property}
              onClick={handleInfoClick}
              buyingStatus={buyingStatus}
              ukResident={ukResident}
              mortgageCalculator={mortgageCalculator}
              showLegalFees={showLegalFees}
            />
          )}

          {property.units && (
            <PropertyUnits
              property={property}
              hasSitemap={hasSitemap}
              onSiteMapClick={() => updateSectionUrl('/sitemap')}
            />
          )}
        </PageWrapper>

        {!property.featuredDevelopmentContent && (
          <AdBox
            id={`PDPmiddle_desktop/PDPmiddle_desktop_${adSuffix}`}
            config="PDPmiddle_desktop"
            sizes={AD_BOX_2_SIZES}
            hideUnder={779}
            obscured={isAdObscuringModalOpen}
            containerStyle={{
              marginTop: 25,
              marginBottom: 45,
              paddingTop: 35,
              paddingBottom: 35,
              background: '#f5f5f5',
            }}
            declaration
          />
        )}

        {!property.featuredDevelopmentContent && (
          <AdBox
            id={`PDPmiddle_mobile/PDPmiddle_mobile_${adSuffix}`}
            config="PDPmiddle_mobile"
            sizes={AD_BOX_3_SIZES}
            hideOver={780}
            obscured={isAdObscuringModalOpen}
            containerStyle={{
              marginTop: 25,
              marginBottom: 45,
              paddingTop: 35,
              paddingBottom: 35,
              background: '#f5f5f5',
            }}
            declaration
          />
        )}

        {property.properties && property.published && (
          <SimilarProperties
            properties={property.properties}
            imageLocator={PROPERTY_IMAGE_LOCATOR[1]}
            onFavourite={onFavourite}
            title="Property Types"
            subTitle={`(${property.totalAvailableTypes} available)`}
            wrapOnDesktop
            cols={3}
          />
        )}

        <PropertySocialBar
          property={property}
          favourited={favourited}
          onFavourite={toggleFavourited}
          onShare={handleShare}
          loggedIn={loggedIn}
        />

        {!property.featuredDevelopmentContent?.hideAdditionalInfo && (
          <PageWrapper className="pp-description-box">
            <PropertyDescription
              property={property}
              onItemClick={handleImageClick}
              onImageClick={(index) => updateSectionUrl('/slideshow', index)}
              images={images}
            />

            {hasFeaturedBrochure && (
              <Button
                onClick={() => routerUpdate(router, `${property.path}/brochure/1`)}
                containerStyle={{ maxWidth: 260, margin: '30px 0' }}
                testID="brochure-btn"
              >
                View Brochure
              </Button>
            )}
          </PageWrapper>
        )}

        <PropertyPdfBar property={property} onItemClick={(path) => routerUpdate(router, `${property.path}${path}`)} />
      </div>

      {property.type === 'property' && property.development && isFeaturedDevelopment && (
        <SimilarProperties
          properties={similarProperties}
          onInView={fetchSimilarProperties}
          title="More from this development"
          background={theme.backgroundLightest}
          hideOnEmpty
          onFavourite={onFavourite}
          showArrows
        />
      )}

      {(!!property.account?.accreditations?.length || !!property.account.missingAccreditations?.length) &&
        property.type === 'property' &&
        !property.featuredDevelopmentContent && (
          <Accordion
            className="hide-print"
            isOpen={!isMobile || openSections.includes('accreditations')}
            header={renderAccordionHeader('accreditations', 'Agent Accreditations')}
          >
            <PageWrapper>
              {!isMobile && <SubHeading mt={60}>Agent Accreditations</SubHeading>}

              <AgentAccreditations
                accreditations={property.account.accreditations}
                missingAccreditations={property.account.missingAccreditations}
              />
            </PageWrapper>
          </Accordion>
        )}

      {mortgageCalculator.hasMortgageCalculator && (
        <>
          <div id="calculator-marker" />

          <Accordion
            className="hide-print"
            isOpen={!isMobile || openSections.includes('mortgageCalculator')}
            header={renderAccordionHeader('mortgageCalculator', 'Mortgage Calculator')}
          >
            <BrokerCalculator property={property} mortgageCalculator={mortgageCalculator} />
          </Accordion>
        </>
      )}

      {showLegalFees && (
        <>
          <div id="legal-fees-marker" />
          <Accordion
            className="hide-print"
            isOpen={!isMobile || openSections.includes('legalFees')}
            header={renderAccordionHeader('legalFees', 'Legal Fees Calculator')}
          >
            <LegalFeesCalculator
              hasContact={hasLegalContact}
              onCalculateClick={() => {
                trackGaEvent('legal_fees_click', { page: 'PDP', location: 'Legal Fees Ad' });
                updateSectionUrl('/legal-fees');
              }}
              property={property}
            />
          </Accordion>
        </>
      )}

      {/* {shouldDisplayGuides(property) && (
          <Accordion
            className="hide-print"
            isOpen={!isMobile || openSections.includes('contentHubGuides')}
            header={renderAccordionHeader('contentHubGuides', 'Home Buying Guides')}
          >
            <ContentHubGuidesPreview />
          </Accordion>
        )} */}

      <div id="broadband-table" />

      {property.ofcomBroadband && (
        <Accordion
          className="hide-print"
          isOpen={!isMobile || openSections.includes('broadbandTable')}
          header={renderAccordionHeader('broadbandTable', 'Broadband Checker')}
        >
          <PageWrapper>
            <BroadbandTable displayAddress={property.addressLine1} broadband={property.ofcomBroadband} />
          </PageWrapper>
        </Accordion>
      )}

      {showSchoolData && (
        <Accordion
          className="hide-print"
          isOpen={!isMobile || openSections.includes('schoolsData')}
          header={renderAccordionHeader('schoolsData', 'Schools Data')}
        >
          <SchoolsData property={property} />
        </Accordion>
      )}

      {/* ensure property is published and is not a development property where we don't show any stats */}
      {property.published && property.type === 'property' && !property.development && (
        <Accordion
          className="hide-print"
          isOpen={!isMobile || openSections.includes('stats')}
          header={renderAccordionHeader('stats', 'Property Statistics')}
        >
          <PropertyStats property={property} showPromoteText={showPromoteText} />
        </Accordion>
      )}

      {property.priceTrackerAvailable && property.published && (
        <Accordion
          className="hide-print"
          isOpen={!isMobile || openSections.includes('marketTrends')}
          header={renderAccordionHeader('marketTrends', 'Northern Ireland House Prices')}
        >
          <MarketTrends
            marketTrendsData={marketTrendsData}
            onInView={fetchMarketTrendsData}
            loading={marketTrendsLoading}
            error={marketTrendsError}
            addressLine1={property.addressLine1}
          />
        </Accordion>
      )}

      {property.type === 'property' && !property.development && (
        <SimilarProperties
          properties={similarProperties}
          onInView={fetchSimilarProperties}
          title="Similar Properties"
          background={theme.backgroundLightest}
          onFavourite={onFavourite}
          emptyText="Sorry, we couldn't find any similar properties to this"
        />
      )}

      {property.featuredDevelopmentContent && (
        <div
          className="pp-content-middle"
          dangerouslySetInnerHTML={{ __html: property.featuredDevelopmentContent.contentMiddle }}
        />
      )}

      {property.type === 'development' && (
        <OtherDevelopments data={otherDevelopments} onInView={fetchOtherDevelopments} />
      )}

      {property.featuredDevelopmentContent && (
        <div
          className="pp-content-bottom"
          dangerouslySetInnerHTML={{ __html: property.featuredDevelopmentContent.contentBottom }}
        />
      )}

      {(property.type === 'development' || property.development) &&
        property.contacts &&
        property.contacts.all.length > 1 &&
        !property.featuredDevelopmentContent?.hideAgentContact &&
        property.published && (
          <ContactTable
            property={property}
            onEnquiryClick={(contact) => {
              trackGaEvent('enquiry_click', {
                location: 'Contact Table',
                account_number: contact.accountNumber,
                page: 'DDP',
              });
              setEnquiryAccountNumber(contact.accountNumber);
              updateSectionUrl('/enquiry');
            }}
            onRegisterInterestClick={(contact) => {
              setEnquiryAccountNumber(contact.accountNumber);
              updateSectionUrl('/register-interest');
            }}
            registerInterest={property.registerInterest}
          />
        )}

      <PageWrapper className="hide-print">
        <Text align="center" mt={30} mb={showPromoteText ? 0 : 30} className="pp-report-a-problem">
          <Bold>SPOTTED A PROBLEM?</Bold> Even the best agents make mistakes, help us to maintain accurate property
          information on our listings. <ReportProblemButton onClick={() => updateSectionUrl('/report-a-problem')} />
        </Text>
        {showPromoteText && <PromoteProperty property={property} label="Small Print" />}
      </PageWrapper>

      <RelatedLinks property={property} />

      <ImageModal
        ariaLabel="EPC image popup"
        show={!!modalImage}
        onClose={() => setModalImage('')}
        imageUrl={modalImage}
      />

      {showStampDutyModal && (
        <StampDutyModal
          buyingStatus={buyingStatus}
          setBuyingStatus={setBuyingStatus}
          ukResident={ukResident}
          setUkResident={setUkResident}
          property={property}
          show={showStampDutyModal}
          onClose={handleBack}
        />
      )}

      {showMapModal && (
        <PropertyMapModal show={showMapModal} property={property} onClose={handleBack} adSuffix={adSuffix} />
      )}

      {showSlideshowModal && (
        <PropertySlideshowModal
          imageIndex={imageIndex}
          setImageIndex={updatePhotoUrl}
          show={showSlideshowModal}
          property={property}
          onClose={handleBack}
          onTabClick={(tab, index) => updateSectionUrl(`/${tab}`, index)}
          url={router.asPath}
          imageLocator={PROPERTY_IMAGE_LOCATOR[0]}
          hasSitemap={hasSitemap}
          adSuffix={adSuffix}
        />
      )}

      {(showEnquiryModal || showRegisterInterestModal || showRequestValuationModal) && (
        <EnquiryModal
          accountNumber={enquiryAccountNumber}
          show={showEnquiryModal || showRegisterInterestModal || showRequestValuationModal}
          onClose={handleBack}
          property={property}
          registerInterestModal={showRegisterInterestModal}
          requestValuationModal={showRequestValuationModal}
          onSuccess={setLeadId}
          loggedIn={loggedIn}
          loggedInUser={property.loggedInUser}
        />
      )}

      {showLegalFeesModal && (
        <LegalFeesModal
          show={showLegalFeesModal}
          onClose={() => {
            // clear result from local storage
            setItem(LEGAL_FEES_RESULT_STORAGE_KEY, null);
            const nextUrl = getReplaceUrl('');
            const [baseUrl, qs] = nextUrl.split('?');

            let url = baseUrl;

            // remove result querystring
            if (qs) {
              const params = new URLSearchParams(qs);
              params.delete('result');

              if (params.toString()) {
                url = `${baseUrl}?${params.toString()}`;
              }
            }

            routerUpdate(router, url);
          }}
          property={property}
          buyingStatus={buyingStatus}
          setBuyingStatus={setBuyingStatus}
          ukResident={ukResident}
          setUkResident={setUkResident}
          source="Legal Calc Form"
        />
      )}

      {showBrochureModal && <BrochureModal show={showBrochureModal} onClose={handleBack} property={property} />}

      {showReportModal && <ReportProblemModal show={showReportModal} onClose={handleBack} property={property} />}

      {showShareModal && (
        <ShareModal show={router.asPath.includes('/share')} onClose={handleBack} property={property} />
      )}

      {modal}

      {property.featuredDevelopmentContent && (
        <style dangerouslySetInnerHTML={{ __html: property.featuredDevelopmentContent.css }} />
      )}
    </div>
  );
};

export default PropertyDetailsContent;
